import { useMemo, useState } from "react"
import { Autocomplete, Box, Button, Stack, TextField, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { If } from "lib/@components/switch"
import { RenderSkillsInput } from "./render-skills-input"
import { Repeat } from "lib/@components/repeat"
import { getAllSkillCodes } from "plugins/skill-codes/controller/get-all-skill-codes"
import { UnmappedSkillChip } from "./unmapped-skill-chip"
import { SelectedSkillRow } from "./selected-skill-row"
import { CustomSkillIndicator } from "./custom-skill-indicator"
import { ConfigureSkillRoutingDialog } from "./configure-skill-routing-dialog"

const EMPTY_ARRAY = []

export function EditMappingFormBody({ allLines, tradeVocabulary }) {
    const { target, refresh } = useBoundContext()
    const [routingSkill, setRoutingSkill] = useState(null)
    const [skillSearch, setSkillSearch] = useState("")
    const allSkills = getAllSkillCodes.useResults() || EMPTY_ARRAY
    const skills = target?.skills || EMPTY_ARRAY
    const tradeOptions = useMemo(buildTradeOptions, [target?.tradeId, target?.tradeName, tradeVocabulary])

    const skillOptions = allSkills
        .map(toSkillOption)
        .filter((s) => !skills.some((sel) => sel.skillId === s.skillId))
        .sort((a, b) => a.skillName.localeCompare(b.skillName))
    const currentUnmapped = useMemo(computeCurrentUnmapped, [allSkills, allLines, skills])

    return (
        <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
                label="Trade Name"
                placeholder="Enter a trade name..."
                data-cy="edit-mapping-trade-name"
                value={target.tradeName || ""}
                onChange={handleTradeInputChange}
            />
            <Stack spacing={1}>
                <Typography variant="subtitle2">Skills</Typography>
                <Autocomplete
                    value={null}
                    inputValue={skillSearch}
                    options={skillOptions}
                    getOptionLabel={getSkillLabel}
                    isOptionEqualToValue={isSkillEqual}
                    onChange={handleAddSkill}
                    onInputChange={handleSkillSearchChange}
                    renderOption={renderSkillOption}
                    renderInput={RenderSkillsInput}
                    disableCloseOnSelect
                />
                <Stack spacing={1}>
                    <Repeat
                        list={skills}
                        item={
                            <SelectedSkillRow
                                onConfigureRouting={handleConfigureRouting}
                                onRemove={handleRemoveSkill}
                            />
                        }
                    />
                </Stack>
            </Stack>
            <If value={currentUnmapped.length}>
                <Stack spacing={1}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="subtitle2">Unmapped Skills ({currentUnmapped.length})</Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleAddAllUnmapped}
                            startIcon={<Iconify icon="mdi:plus" />}
                        >
                            Add All
                        </Button>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        <Repeat list={currentUnmapped} item={<UnmappedSkillChip onAdd={handleAddUnmappedSkill} />} />
                    </Box>
                </Stack>
            </If>
            <If value={!!routingSkill}>
                <ConfigureSkillRoutingDialog
                    open={!!routingSkill}
                    skillName={routingSkill?.skillName}
                    skillCode={routingSkill?.skillCode}
                    initialTradeId={routingSkill?.tradeId || target.tradeId}
                    defaultTradeName={target.tradeName}
                    initialConditionals={routingSkill?.conditionalTrades || []}
                    tradeOptions={tradeOptions}
                    onClose={closeRouting}
                    onResult={handleRoutingResult}
                />
            </If>
        </Stack>
    )

    function handleTradeInputChange(e) {
        target.tradeName = e.target.value
        refresh()
    }

    function handleAddSkill(e, newValue) {
        if (!newValue) return
        target.skills = [...skills, newValue]
        setSkillSearch("")
        refresh()
    }

    function handleSkillSearchChange(e, newValue, reason) {
        if (reason !== "reset") setSkillSearch(newValue)
    }

    function handleRemoveSkill(skillId) {
        target.skills = skills.filter((s) => s.skillId !== skillId)
        refresh()
    }

    function handleAddUnmappedSkill(skill) {
        target.skills = [...skills, skill]
        refresh()
    }

    function handleAddAllUnmapped() {
        target.skills = [...skills, ...currentUnmapped]
        refresh()
    }

    function handleConfigureRouting(skill) {
        setRoutingSkill(skill)
    }

    function closeRouting() {
        setRoutingSkill(null)
    }

    function handleRoutingResult(result) {
        target.skills = buildSkillsWithRoutingResult(result)
        closeRouting()
        refresh()
    }

    function buildSkillsWithRoutingResult(result) {
        const nextSkills = []
        for (const skill of skills) {
            nextSkills.push(applyRoutingResult(skill, result))
        }
        return nextSkills
    }

    function applyRoutingResult(skill, result) {
        if (skill.skillId !== routingSkill?.skillId) return skill
        return buildSkillWithRoutingResult(skill, result)
    }

    function buildSkillWithRoutingResult(skill, result) {
        return { ...skill, tradeId: result.tradeId || null, conditionalTrades: result.conditionalTrades || [] }
    }

    function buildTradeOptions() {
        const options = Object.entries(tradeVocabulary || {})
        if (!target?.tradeId || !target?.tradeName) return options
        if (options.some(([id]) => id === target.tradeId)) return options
        return [[target.tradeId, target.tradeName], ...options]
    }

    function computeCurrentUnmapped() {
        if (!allSkills.length) return []
        const mappedIds = new Set((allLines || []).filter(hasTradeId).map(toSkillId))
        const currentIds = new Set(skills.map(toSkillId))
        return allSkills.map(toSkillOption).filter((s) => !mappedIds.has(s.skillId) && !currentIds.has(s.skillId))
    }
}

function hasTradeId(line) {
    return line.tradeId
}

function toSkillId(item) {
    return item.skillId
}

function toSkillOption(skill) {
    return {
        skillId: skill.CoreSkillingID,
        skillName: skill.Skilling,
        skillCode: skill.SkillingCode || "",
        isCustomSkill: !!skill.client,
        customHourlyRate: skill.Rate,
    }
}

function renderSkillOption(props, option) {
    return (
        <Box component="li" {...props}>
            <Box sx={{ flex: 1 }}>{formatSkillLabel(option)}</Box>
            <If value={option.isCustomSkill}>
                <CustomSkillIndicator rate={option.customHourlyRate} />
            </If>
        </Box>
    )
}

function getSkillLabel(option) {
    return formatSkillLabel(option)
}

function formatSkillLabel(skill) {
    if (skill.skillCode) return `${skill.skillName} (${skill.skillCode})`
    return skill.skillName
}

function isSkillEqual(option, value) {
    return option.skillId === value.skillId
}
